import { ColDef, RowClickedEvent, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { useSAL1010_R01Store, } from "./Store_SAL1010_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import { defaultSAL1010_R01HeaderDto, SAL1010_R01HeaderDto } from "./Dto_SAL1010_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL1010_R01-grid-column-state';

// 숫자 컬럼 목록
const numberFields: (keyof SAL1010_R01HeaderDto)[] = ['orderAmt'];

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL1010_R01HeaderDto> | null>;
	defaultSAL1010_R01HeaderRow: SAL1010_R01HeaderDto;
	handleCreateClick: () => void;
}

export const Table_SAL1010_R01 = (props: Props) => {
	const { gridRef, defaultSAL1010_R01HeaderRow, handleCreateClick } = props;

	const { 
		SAL1010_R01List, 
		selectedSAL1010_R01, setSelectedSAL1010_R01, initSelectedSAL1010_R01,
		columnState, setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useSAL1010_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'salesOrderNo',
		gridData: SAL1010_R01List,
		defaultGridData: defaultSAL1010_R01HeaderRow,
		selectedRow: selectedSAL1010_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		orderDt: '수주일자',
		customNm: '거래처명',
		orderAmt: '주문금액',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'customNm')(params), []);

	const baseColumnDefs: ColDef<SAL1010_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '수주일자', colId: 'orderDt', field: 'orderDt', width: 120,
			cellStyle: (p) => getCellStyle(p, 'orderDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (p: ValueFormatterParams<SAL1010_R01HeaderDto>) =>
				p.data?.orderDt ? formatDate(new Date(p.data.orderDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '거래처명', colId: 'customNm', field: 'customNm', width: 180,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '주문금액', colId: 'orderAmt', field: 'orderAmt', width: 120,
			cellStyle: (p) => getCellStyle(p, 'orderAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '수주상태', colId: 'orderStatus', field: 'orderStatus', width: 100,
			cellStyle: (p) => getCellStyle(p, 'orderStatus'),
			headerClass: aggridHeaderClass,
			valueFormatter: (params: ValueFormatterParams<SAL1010_R01HeaderDto>) => {
				if (params.node?.rowPinned === 'bottom') return '';
				if (params.value === 'E') return '출고완료';
				if (params.value === 'C') return '취소';
				if (params.value === 'P') return '출고진행중';
				if (params.value === 'N') return '출고대기';
				return '';
			},
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL1010_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL1010_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<SAL1010_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		const isSelected = selectedSAL1010_R01?.salesOrderNo === rowData.salesOrderNo;
		if (isSelected) {
			setSelectedSAL1010_R01(undefined);
			setDetailScrollTop(null);
			handleCreateClick();
		} else {
			setDetailScrollTop(null);
			setSelectedSAL1010_R01(rowData);
		}
	}, [selectedSAL1010_R01, setSelectedSAL1010_R01, setDetailScrollTop, handleCreateClick]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		customNm: SAL1010_R01List.length.toLocaleString() + "건",
		orderAmt: SAL1010_R01List.reduce((sum, item) => (sum + (item.orderAmt || 0)), 0).toLocaleString(),
		editable: false,
	}]), [SAL1010_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [SAL1010_R01List, selectedSAL1010_R01]);

	return (
		<GridContextWrapper<SAL1010_R01HeaderDto>	
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSAL1010_R01}
			defaultRowData={defaultSAL1010_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SAL1010_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs} 
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<SAL1010_R01HeaderDto>({
					idKey: 'salesOrderNo',
					selectedList: selectedSAL1010_R01 ? [selectedSAL1010_R01.salesOrderNo] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
}; 
